import React, { useState, useEffect, useRef } from "react";
import Select, { components } from "react-select";
import { CheckIcon, RefreshIcon } from "./Svg";
import { useSelector, useDispatch } from "react-redux";
import Ai from "../store/models/Ai";
import { selectCurrentProjectId } from "../store/models/Project";
import useDebounce from "../hooks/useDebounce";
import { createPortal } from 'react-dom';
import { selectMobileLayout } from "../store/UIState";
import { Accordion, AccordionHeader } from "./Accordion";

export const CommunityNodeSearchBar = ({ orderBy, setOrderBy, loading, setLoading }) => {
  const dispatch = useDispatch();
  const projectId = useSelector(state => selectCurrentProjectId(state));
  const isMobile = useSelector(state => selectMobileLayout(state));
  const [gpuType, setGpuType] = useState([]);
  const [gpuCount, setGpuCount] = useState(0);
  const [vRam, setVRam] = useState(0);
  const [mem, setMem] = useState(0);
  const [disk, setDisk] = useState(0);
  const [price, setPrice] = useState(0);
  const [gpuTypeOptions, setGpuTypeOptions] = useState([{ label: 'All', value: 'all' }])

  useEffect(() => {
    fetchList();

    async function fetchList() {
      let res = await dispatch(Ai.actions.fetchCommunityFilterVMs(projectId));
      let newOptions = res.map(v => ({ label: getGpuLabel(v), value: v }))
        .sort((a, b) => b.label.localeCompare(a.label))
        .concat([{ label: 'All', value: 'all' }]);
      setGpuTypeOptions(newOptions)
    }
  }, [])

  const onOrderChange = (v) => {
    setOrderBy(v);
  }

  const handleGpuTypeChange = (selected) => {
    if (!selected || selected.length === 0) {
      setGpuType([]);
      return;
    }

    const wasAllSelected = gpuType.some(opt => opt.value === 'all');
    const isAllSelectedNow = selected.some(opt => opt.value === 'all');

    if (!wasAllSelected && isAllSelectedNow) {
      setGpuType(gpuTypeOptions);
      return;
    }

    if (wasAllSelected && selected.length === gpuTypeOptions.length - 1) {
      if (isAllSelectedNow) {
        const updated = selected.filter(opt => opt.value !== 'all');
        setGpuType(updated);
        return;
      }
      setGpuType([]);
      return;
    }

    setGpuType(selected);
  };

  const debounceFetch = useDebounce(async (mem, disk, gpuCount, gpuType, price, vRam) => {
    const gpuString = (gpuType || []).map(o => o.value).join('|');
    const filter = {
      mem: mem * 1024 ** 3,
      disk: disk * 1024 ** 3,
      gpuCount,
      gpu: gpuString,
      price: price * 10000,
      vRam: vRam * 1024
    }
    try {
      await dispatch(Ai.actions.fetchCommunityVMs(projectId, filter));
    } catch (e) {
    } finally {
    }

  }, 1000)

  useEffect(() => {
    debounceFetch(mem, disk, gpuCount, gpuType, price, vRam);
  }, [gpuCount, gpuType, mem, disk, price, vRam])

  const handleReset = () => {
    if (gpuType.length) setGpuType([]);
    if (gpuCount) setGpuCount(0);
    if (vRam) setVRam(0);
    if (mem) setMem(0);
    if (disk) setDisk(0);
    if (price) setPrice(0);
  }
  return <div className='ManageGpuNodeModal__row communityNodeSearchBar'>
    <div className="communityNodeSearchBar__order">
      <Select
        value={orderBy}
        onChange={onOrderChange}
        styles={selectStyles}
        options={orderOptions}
        placeholder="Order by"
        components={{ SingleValue: CustomSingleValue }}
        className="communityNodeSearchBar__select"
      />
      {isMobile && <>
        <Accordion
          header={<AccordionHeader
            title="Filters"
          />}
          body={<>
            <MultiSelect value={gpuType} handleValueChange={handleGpuTypeChange} options={gpuTypeOptions} title={'GPU Type'} />
            <RangeSelect value={gpuCount} setValue={setGpuCount} type='gpu-count' />
            <RangeSelect value={vRam} setValue={setVRam} type='vRam' />
            <RangeSelect value={mem} setValue={setMem} type='mem' />
            <RangeSelect value={disk} setValue={setDisk} type='disk' />
            <RangeSelect value={price} setValue={setPrice} type='price' />
            <div className="communityNodeSearchBar__search--button" onClick={handleReset}>
              <RefreshIcon />
            </div>
          </>} />
      </>}
    </div>
    {!isMobile && <>
      <div className="communityNodeSearchBar__filter">
        <MultiSelect value={gpuType} handleValueChange={handleGpuTypeChange} options={gpuTypeOptions} title={'GPU Type'} />
        <RangeSelect value={gpuCount} setValue={setGpuCount} type='gpu-count' />
        <RangeSelect value={vRam} setValue={setVRam} type='vRam' />
        <RangeSelect value={mem} setValue={setMem} type='mem' />
        <RangeSelect value={disk} setValue={setDisk} type='disk' />
        <RangeSelect value={price} setValue={setPrice} type='price' />
      </div>
      <div className="communityNodeSearchBar__search">
        <div className="communityNodeSearchBar__search--button" onClick={handleReset}>
          <RefreshIcon />
        </div>
      </div>
    </>}
  </div>
}

const orderOptions = [{ label: 'Price (inc)', value: 'price_inc' },
{ label: 'Price (dec)', value: 'price_dec' },
{ label: 'vRAM size', value: 'vram' },
{ label: 'MEM size', value: 'mem' },
{ label: 'Disk size', value: 'disk' }]

// const gpuTypeOptions = [{ label: 'H100', value: 'h100' },
// { label: 'A100', value: 'a100' },
// { label: 'RTX 5090', value: 'NVIDIA-GeForce-RTX-5090' },
// // { label: 'RTX 5070', value: 'NVIDIA-GeForce-RTX-5070' },
// // { label: 'RTX 4090', value: 'NVIDIA-GeForce-RTX-4090' },
// { label: 'RTX 4070', value: 'NVIDIA-GeForce-RTX-4070' },
// { label: 'RTX 3070', value: 'NVIDIA-GeForce-RTX-3070' },
// { label: 'Tesla T4', value: 'Tesla-T4' },
// { label: 'CPU only', value: 'cpu-only' },
// { label: 'All', value: 'all' }]

const selectStyles = {
  control: (provided, state) => {
    const value = state.selectProps.currentValue;
    return {
      ...provided,
      backgroundColor: '#191D29',
      borderColor: (state.hasValue || value) ? '#18C99D' : '#3D4463',
      borderRadius: '4px',
      color: '#8A8FB5',
      outline: 'none',
      minHeight: 'none',
      boxShadow: 'none',
      ':hover': {
        borderColor: '#18C99D',
      },
    }
  },
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isDisabled ? '#131722' :
      state.isFocused ? 'rgba(24, 201, 157, 0.1)' : '#131722',
    color: state.isDisabled ? '#FFFFFF' :
      state.isFocused ? '#18C99D' :
        state.isSelected ? '#18C99D' : '#8A8FB5',
    fontFamily: 'Gilroy',
    paddingLeft: '10px',
    fontWeight: '500',
    fontSize: '14px',
    paddingTop: '11.5px',
    paddingBottom: '11.5px',
    height: 'auto',
    position: 'relative',
    cursor: state.isDisabled ? 'default' : 'pointer',
    width: 'auto',
    minWidth: 'max-content',
    ':hover': {
      backgroundColor: state.isDisabled ? '#131722' : 'rgba(24, 201, 157, 0.1)',
    },
    whiteSpace: 'nowrap',
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    display: 'none'
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: '#8A8FB5',
    marginRight: '0',
    marginLeft: '4px',
    padding: '8px 8px 8px 0',
    ':hover': {
      color: '#8A8FB5',
      cursor: 'pointer'
    },
  }),
  menu: (provided, state) => ({
    ...provided,
    backgroundColor: '#131722',
    borderRadius: '4px',
    border: '1px solid #3D4463',
    scrollbarColor: '#3D4463',
    marginTop: '5px',
    marginBottom: '0px',
    zIndex: 3,
    width: 'auto',
    minWidth: 'max-content',
  }),
  menuList: (provided, state) => ({
    ...provided,
    maxHeight: '280px',
    zIndex: 5,
  }),
  input: (provided) => ({
    ...provided,
    height: 0,
    minHeight: 0,
    padding: 0,
    margin: 0,
    border: 0,
    position: 'absolute',
    zIndex: -1,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 0 2px 8px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#8A8FB5',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    margin: '0',
  })
}


const CustomSingleValue = ({ children, ...props }) => (
  <>
    <div className="custom-placeholder">
      {props.selectProps.placeholder}
    </div>
    <div style={{ height: 0, overflow: 'hidden', position: 'absolute' }}>
      {children}
    </div>
  </>
);

const CheckboxOption = (props) => {
  const { isSelected, label, innerRef, innerProps } = props;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        cursor: 'pointer',
        width: 'auto',
      }}
    >
      <div className="communityNodeSearchBar__icon-container">
        {isSelected && (
          <CheckIcon />
        )}
      </div>
      <span style={{ color: '#8A8FB5' }}>{label}</span>
    </div>
  );
};

function RangeSelect({ value, setValue, type }) {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const selectRef = React.useRef(null);
  const title = TitleMap[type];

  const handleApply = () => {
    setIsPopoverOpen(false);
  };

  return (
    <div className="communityNodeSearchBar__range-select" ref={selectRef}>
      <Select
        value={null}
        options={[]}
        placeholder={title}
        menuIsOpen={false}
        isClearable={false}
        components={{
          DropdownIndicator: CustomIndicator,
          MultiValue: () => null,
          ValueContainer: CustomRangeValueContainer,
        }}
        styles={selectStyles}
        isPopoverOpen={isPopoverOpen}
        setIsPopoverOpen={setIsPopoverOpen}
        currentValue={value}
      />

      <PopoverPanel
        isOpen={isPopoverOpen}
        anchorRef={selectRef}
        value={value}
        setValue={setValue}
        onApply={handleApply}
        type={type}
        title={title}
      />
    </div>
  );
}


const PopoverPanel = ({ isOpen, anchorRef, value, setValue, onApply, type }) => {
  const popoverRef = useRef(null);
  const isMobile = useSelector(state => selectMobileLayout(state));
  const [position, setPosition] = useState({ top: 0, left: 0, width: 'auto' });

  const updatePosition = () => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: isMobile ? rect.width : 'auto'
      });
    }
  };

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isOpen &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        !anchorRef.current.contains(e.target)
      ) {
        onApply();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    }
  }, [isOpen, anchorRef, onApply]);

  const getBackground = (value, min, max) => {
    const v = ((value - min) / (max - min)) * 100;
    return `linear-gradient(to right, #3D4463 ${v}%, #129978 ${v}%)`;
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className="communityNodeSearchBar__popoverPanel"
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        width: position.width,
        zIndex: 1000
      }}
    >
      <div className="communityNodeSearchBar__popoverPanel--title">
        Set Minimal {TitleMap[type]}
      </div>
      <div className="communityNodeSearchBar__popoverPanel--slider">
        <div className="communityNodeSearchBar__popoverPanel--range">
          <div className="communityNodeSearchBar__popoverPanel--value">
            {(type === 'price') ? '$' : ''}
            {value}
            {(type === 'mem' || type === 'disk' || type === 'vRam') ? 'GB' : ''}
          </div>
        </div>
        <input
          type="range"
          step={StepMap[type]}
          max={MaxMap[type]}
          min={MinMap[type]}
          value={value}
          onChange={e => {
            setValue(e.target.value);
          }}
          style={{ background: getBackground(value, MinMap[type], MaxMap[type]) }}
        />
      </div>
    </div>,
    document.body
  );
};


const MultiSelect = ({ value, handleValueChange, options, title }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref}>
      <Select
        isMulti
        value={value}
        onChange={(val) => {
          handleValueChange(val);
          setMenuOpen(true);
        }}
        options={options}
        placeholder={title}
        styles={selectStyles}
        menuIsOpen={menuOpen}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        closeMenuOnSelect={false}
        isClearable={false}
        hideSelectedOptions={false}
        components={{
          Option: CheckboxOption,
          MultiValue: () => null,
          MultiValueRemove: () => null,
          ValueContainer: CustomMultiValueContainer,
        }}
        className="communityNodeSearchBar__select gpu-type"
      />
    </div>
  );
};

const CustomRangeValueContainer = ({ children, ...props }) => {
  const { selectProps } = props;

  const handleClick = (e) => {
    e.stopPropagation();
    if (selectProps.setIsPopoverOpen) {
      selectProps.setIsPopoverOpen(v => !v);
    }
  };

  return (
    <components.ValueContainer {...props}>
      <div
        onClick={handleClick}
        onTouchStart={handleClick}
        className="custom-placeholder"
        style={{ width: '100%', cursor: 'pointer' }}
      >
        {selectProps.placeholder}
      </div>
    </components.ValueContainer>
  );
};

const CustomMultiValueContainer = ({ children, ...props }) => {
  const { selectProps } = props;

  const handleClick = (e) => {
    e.stopPropagation();
    if (selectProps.setMenuOpen) {
      selectProps.setMenuOpen(true);
    }
  };

  return (
    <components.ValueContainer {...props}>
      <div
        onClick={handleClick}
        className="custom-placeholder"
        style={{ width: '100%', cursor: 'pointer' }}
      >
        {selectProps.placeholder}
      </div>
      <div style={{ height: 0, overflow: 'hidden', position: 'absolute' }}>{children}</div>
    </components.ValueContainer>
  );
};


const CustomIndicator = (props) => {
  const { isPopoverOpen, setIsPopoverOpen } = props.selectProps;

  return (
    <components.DropdownIndicator {...props}>
      <div
        onMouseDown={(e) => {
          e.preventDefault();
          setIsPopoverOpen(!isPopoverOpen);
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          setIsPopoverOpen(!isPopoverOpen);
        }}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" fill="currentColor"></path>
        </svg>
      </div>
    </components.DropdownIndicator>
  );
};

const STEP = 1;
const MIN = 1;
const MAX = 100;

const TitleMap = {
  'gpu-count': 'GPU Count',
  'vRam': 'vRAM Size',
  'mem': 'Mem Size',
  'disk': 'Disk Size',
  'price': 'Price range'
}
const MaxMap = {
  'gpu-count': 50,
  'vRam': 20,
  'mem': 128,
  'disk': 4096,
  'price': 2
}
const MinMap = {
  'gpu-count': 1,
  'vRam': 1,
  'mem': 1,
  'disk': 16,
  'price': 0.01
}
const StepMap = {
  'gpu-count': 1,
  'vRam': 1,
  'mem': 1,
  'disk': 16,
  'price': 0.01
}

const getGpuLabel = (v) => {
  return v.replaceAll('NVIDIA-GeForce-', '').replaceAll('-', ' ');
}

{/* <div modal-content>
  <div __row>
    <div></div>
    <div __filter>
      <div position:relative>
        <div></div>
        <div popoverPanel></div>
      </div>
    </div>
    <div></div>
  </div>
</div> */}